import { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Collapse } from '@material-ui/core';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import currencySymbol from '../../helpers/currencySymbol';

const ExpandableRow = ({
  title,
  subtitle,
  price,
  priceSubtitle,
  currencyCode,
  oldPrice,
  children,
  doShowPricing,
  bookingwarning,
  pnrAAFacts,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { track } = useAnalytics();

  const handleExpand = () => {
    setIsExpanded((previous) => !previous);
    track('expandableRow.handleExpand', {
      price,
    });
  };

  return (
    <section>
      <div className="md:grid grid-cols-3 text-sm xl:text-md pt-2">
        <div className="col-span-2">
          {title}
          <div className={clsx('sm:text-xs xl:text-sm text-gray-600', { 'col-span-3': !priceSubtitle, 'col-span-2': priceSubtitle })}>
            {subtitle}
          </div>
        </div>
        <div className={clsx('row-start-1 row-end-2 col-start-3 col-end-4 text-right', { 'mr-2': !children })}>
          {doShowPricing && oldPrice > 0 && price !== oldPrice && (
          <NumberFormat
            value={oldPrice}
            prefix={currencySymbol(currencyCode)}
            displayType="text"
            thousandSeparator
            fixedDecimalScale="true"
            decimalScale="2"
            className="line-through pr-1"
          />
          )}
          {doShowPricing && (
          <NumberFormat
            value={price}
            prefix={currencySymbol(currencyCode)}
            displayType="text"
            thousandSeparator
            fixedDecimalScale="true"
            decimalScale="2"
            className="block"
          />
          )}
          {priceSubtitle && (
          <div className="col-start-3 text-right text-xs text-gray-600">
            {priceSubtitle}
          </div>
          )}
          {children ? (
            <button
              type="button"
              onClick={handleExpand}
              className="focus:outline-none text-xs text-theme"
            >
              View Details
              {isExpanded
                ? <FaChevronUp className="inline-block ml-0.5 text-xs" />
                : <FaChevronDown className="inline-block ml-0.5 text-xs" />}
            </button>
          ) : <></>}
        </div>
        {bookingwarning && (
        <div className="col-span-3">
          <span className="col-span-3 text-red-500">
            {bookingwarning}
          </span>
        </div>
        )}
        {pnrAAFacts && (
        <div className="col-span-3">
          <span className="col-span-3 text-red-500">
            {pnrAAFacts}
          </span>
        </div>
        )}
      </div>
      {isExpanded && (
        <Collapse in={isExpanded}>
          <div>
            {children}
          </div>
        </Collapse>
      )}
    </section>
  );
};

ExpandableRow.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  subtitle: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  price: PropTypes.number,
  priceSubtitle: PropTypes.string,
  oldPrice: PropTypes.number,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  doShowPricing: PropTypes.bool,
  currencyCode: PropTypes.string,
  bookingwarning: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  pnrAAFacts: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
};

ExpandableRow.defaultProps = {
  subtitle: undefined,
  price: undefined,
  oldPrice: undefined,
  priceSubtitle: undefined,
  children: undefined,
  doShowPricing: true,
  currencyCode: 'USD',
  bookingwarning: undefined,
  pnrAAFacts: undefined,
};

export default ExpandableRow;
